import { resolveId } from '@medplum/core'
import { featureFlags } from '@seen/core/featureFlags'
import type * as appointments from '@seen/core/models/appointments/index'
import * as healthcareServices from '@seen/core/models/healthcareService/index'
import { Order } from '@seen/core/models/orders/Order'
import {
  getFilenameFromContentDisposition,
  saveBlobWithName,
} from '@seen/core/utils'
import { Button } from '@seen/ui'
import { IconPrinter } from '@tabler/icons-react'
import { useMutation } from '@tanstack/react-query'
import type { JSX } from 'react'
import { packetReportReferences } from '#src/components/appointment/packetReportReferences.ts'
import { useSeenApi } from '#src/contexts/seen-api/useSeenApi.ts'
import { useConfirmationStore } from '#src/hooks/useConfirmationStore.ts'
import { useFeatureFlag } from '#src/hooks/useFeatureFlag.ts'
import { useRead } from '#src/hooks/useRead.ts'
import { useSearch } from '#src/hooks/useSearch.ts'
import { toast } from '#src/utils/toast.ts'

export function PrintAppointmentPrepPacketButton({
  appointment,
}: {
  appointment: appointments.ExtendedAppointment
}): JSX.Element | null {
  const enabled = useFeatureFlag(featureFlags.appointmentPrepPacket)
  const { data: service } = useRead(
    healthcareServices.ExtendedHealthcareService,
    enabled ? appointment.healthcareService : undefined,
    { silentErrors: true }
  )
  const seenApi = useSeenApi().seenApi
  const openConfirmation = useConfirmationStore((state) => state.open)
  const { data: orders = [] } = useSearch(
    Order,
    { _id: appointment.basedOn?.map(resolveId) },
    {
      query: { enabled: enabled && !!appointment.basedOn?.length },
      silentErrors: true,
    }
  )

  const printPacket = useMutation({
    mutationFn: async () => {
      const response = await seenApi.post(
        `/pdf/appointments/${appointment.id}/prep-packet`,
        {},
        { responseType: 'blob' }
      )
      const filename = getFilenameFromContentDisposition(
        response.headers['content-disposition']
      )
      saveBlobWithName(response.data, filename ?? 'prep-packet.pdf')
    },
    onMutate: () => {
      toast.loading('Preparing prep packet…', { id: 'prep-packet' })
    },
    onSettled: () => {
      toast.dismiss('prep-packet')
    },
    onError: () => {
      toast.error('Failed to prepare prep packet')
    },
  })

  const print = () => {
    const reportReferences = packetReportReferences(appointment, orders)
    if (reportReferences.length === 0) {
      printPacket.mutate()
      return
    }
    openConfirmation({
      title: 'Extra reports may be needed',
      description: (
        <span className="whitespace-pre-line">
          {`The appointment or order asks for reports that are not added to this packet automatically. Review these instructions and add the reports before giving the packet to the specialist office.\n\n${reportReferences
            .map((reference) => `${reference.source}: ${reference.text}`)
            .join(
              '\n'
            )}\n\nSelect Confirm to create the packet without those reports.`}
        </span>
      ),
      onConfirm: () => printPacket.mutate(),
    })
  }

  if (
    !enabled ||
    !service?.isExternal ||
    !appointment.patient ||
    appointment.isCancelled
  ) {
    return null
  }

  return (
    <Button
      className="w-full"
      variant="outline"
      size="sm"
      onClick={print}
      disabled={printPacket.isPending}
    >
      <IconPrinter className="size-4" />
      {printPacket.isPending ? 'Preparing packet…' : 'Print Specialist Packet'}
    </Button>
  )
}
